import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { ContentCard } from '@/features/subscriber/ContentCard';
import { FollowButton } from '@/features/subscriber/FollowButton';
import { subscriberQueries } from '@/lib/queries/subscriber';

export const Route = createFileRoute('/subscriber/creators/$username')({
  component: CreatorProfileRoute,
});

function CreatorProfileRoute() {
  const { username } = Route.useParams();
  const creator = useQuery(subscriberQueries.creatorByUsername(username));
  const content = useQuery(subscriberQueries.creatorContent(creator.data?.id));
  const [tab, setTab] = useState<'essays' | 'videos' | 'about'>('essays');

  if (creator.isLoading) {
    return <p className="font-sans text-sm text-ink/50">Loading…</p>;
  }
  const c = creator.data;
  if (!c) {
    return (
      <div>
        <p className="font-sans text-sm text-ink/70">No such contributor.</p>
        <Link
          to="/subscriber/contributors"
          className="mt-4 inline-block font-sans text-sm text-oxblood hover:underline"
        >
          ← All contributors
        </Link>
      </div>
    );
  }

  const items = content.data ?? [];
  const essays = items.filter((i) => i.type === 'article');
  const videos = items.filter((i) => i.type === 'video');

  return (
    <div className="mx-auto max-w-4xl">
      <header className="flex items-center gap-5">
        {c.avatar_url ? (
          <img src={c.avatar_url} alt="" className="h-20 w-20 rounded-full object-cover" />
        ) : (
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-ink/10 font-serif text-2xl text-ink/50">
            {c.display_name?.[0]?.toUpperCase() ?? '·'}
          </div>
        )}
        <div className="flex-1">
          <h1 className="font-serif text-3xl text-ink">{c.display_name}</h1>
          <p className="font-sans text-sm text-ink/50">@{c.username}</p>
        </div>
        <FollowButton creatorId={c.id} />
      </header>

      <div className="mt-8 flex gap-1 border-b border-ink/10">
        {(
          [
            ['essays', `Essays (${essays.length})`],
            ['videos', `Videos (${videos.length})`],
            ['about', 'About'],
          ] as const
        ).map(([val, lbl]) => (
          <button
            key={val}
            type="button"
            onClick={() => setTab(val)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm ${
              tab === val
                ? 'border-oxblood text-ink'
                : 'border-transparent text-ink/60 hover:text-ink'
            }`}
          >
            {lbl}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === 'about' ? (
          <p className="font-sans text-base text-ink/80">
            {c.bio ?? 'This contributor has not written a bio yet.'}
          </p>
        ) : (
          (() => {
            const shown = tab === 'essays' ? essays : videos;
            return shown.length === 0 ? (
              <p className="text-center font-sans text-sm text-ink/50">
                Nothing published here yet.
              </p>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {shown.map((item) => (
                  <ContentCard key={item.content_id} item={item} />
                ))}
              </div>
            );
          })()
        )}
      </div>
    </div>
  );
}
